import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faRocket } from '@/fontawesome';
import type { PreviewCoreRecommendation, PreviewEarningsRecommendation } from '@/models';
import type { TradingRecommendationType } from '@/modelsLocal/trading-recommendation';

interface PreviewRecommendationCardProps {
  preview: PreviewCoreRecommendation | PreviewEarningsRecommendation;
  type: TradingRecommendationType;
}

export default function PreviewRecommendationCard({ preview, type }: PreviewRecommendationCardProps) {
  return (
    <div className={`relative bg-white dark:bg-gray-800 rounded-lg p-3 lg:p-6 overflow-hidden ${
      preview.boost
        ? 'border-2 border-amber-400 dark:border-amber-500 shadow-lg shadow-amber-200/50 dark:shadow-amber-900/30'
        : 'border border-gray-200 dark:border-gray-700'
    }`}>
      {/* Blurred placeholder content */}
      <div className="blur-md select-none pointer-events-none">
        <div className="items-start gap-2 lg:gap-3 grid sm:grid md:grid lg:flex">
          {/* Chart placeholder - hidden on mobile */}
          <div className="hidden lg:block lg:w-[180px] lg:flex-none">
            <div className="w-full h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>

          {/* Details container */}
          <div className="flex-1 grid gap-3 grid-cols-3 lg:grid-cols-16">
            {/* Symbol placeholder */}
            <div className="col-span-1 lg:col-span-2">
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Symbol</div>
              <div className="text-lg font-semibold text-gray-400 dark:text-gray-500">████</div>
              {/* Badges placeholder */}
              <div className="flex flex-wrap gap-1 mt-1">
                {preview.boost && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400">
                    <FontAwesomeIcon icon={faRocket} className="w-3 h-3 mr-1" />
                    BOOST
                  </span>
                )}
              </div>
            </div>

            {/* Sector placeholder - hidden on mobile */}
            <div className="hidden lg:block lg:col-span-4">
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Branche</div>
              <div className="text-base text-gray-400 dark:text-gray-500">████████████</div>
            </div>

            {/* Strike Price placeholder */}
            <div className="col-span-1 lg:col-span-4">
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Strike</div>
              <div className="text-base text-gray-400 dark:text-gray-500 tabular-nums">$███.██</div>
            </div>

            {/* Premium placeholder */}
            <div className="col-span-1 lg:col-span-4">
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Prämie</div>
              <div className="text-base text-gray-400 dark:text-gray-500 tabular-nums">$██.██</div>
              <div className="text-xs text-gray-400 dark:text-gray-500">(█.██%)</div>
            </div>

            {/* Start Date placeholder */}
            <div className="hidden lg:block lg:col-span-2">
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Startdatum</div>
              <div className="text-base text-gray-400 dark:text-gray-500 tabular-nums">██.██.████</div>
            </div>

            {/* End Date placeholder */}
            <div className="hidden lg:block lg:col-span-2">
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Enddatum</div>
              <div className="text-base text-gray-400 dark:text-gray-500 tabular-nums">██.██.████</div>
            </div>

            {/* Status placeholder */}
            <div className="hidden lg:block lg:col-span-2">
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Status</div>
              <div className="w-6 h-6 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Lock overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-white/90 via-white/50 to-transparent dark:from-gray-800/90 dark:via-gray-800/50 dark:to-transparent flex items-end justify-center pb-4 rounded-lg">
        <div className="text-center bg-white/80 dark:bg-gray-800/80 px-4 py-2 rounded-lg shadow-sm">
          <FontAwesomeIcon icon={faLock} className="w-5 h-5 text-amber-500 dark:text-amber-400 mb-1" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {type === 'CORE' ? 'Kernstrategie' : 'Earnings'} Premium
          </p>
        </div>
      </div>
    </div>
  );
}
